import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { catchError, map, Observable, of, take, tap } from 'rxjs';
import { PropertySet, PropertySetPermission } from '@miq/domain/property-sets';
import { Filter, IOption } from '@cbre/components';
import { PropertySetColumnStateService } from '../../property-set-column/property-set-column.state';
import { PropertySetOperation, PropertySetOperationsTrackerService } from '../property-set-operations-tracker.service';
import { TogglePointFunctionHelper } from '@cbre/feature-toggles';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

// TBD
export interface PropertySetQuery extends Record<string, any> {
  name?: string;
}

export interface DataObject {
  [key: string]: any;
}

export interface RecordsResponse {
  totalCount: number;
  results: DataObject[];
}
export interface SortField {
  field?: string;
  order?: string;
  dataType?: string;
}

export interface PSSearchQuery {
  userId: number;
  entity: string;
  filters: Filter[];
  limit: number;
  offset?: number;
  geography?: Record<string, any>;
  sort?: SortField;
  multiSort?: SortField[];
  after?: any[];
  showAllUsages?: boolean;
}

export interface HistoricalStatQuery {
  query: any;
  options: any;
  type: string;
  isClipBoard?: boolean;
  exportId?: number;
  propertySetRecordId?: string;
  isHistorical?: boolean;
  periodType?: string;
}

export function PSSearchQueryFactory(options: Partial<PSSearchQuery>): PSSearchQuery {
  const defaults = { entity: 'property', offset: 0, limit: 0, filters: [], showAllUsages: false };
  const userId = JSON.parse(localStorage.getItem('userInfo'))?.id;
  return { ...defaults, ...options, userId };
}

type PropertySetDto = Omit<PropertySet, 'id'>;

@Injectable({ providedIn: 'root' })
export class PropertySetsHttpService {
  constructor(
    private client: HttpClient,
    private psColumnState: PropertySetColumnStateService,
    private psOperatinsTracker: PropertySetOperationsTrackerService,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {}

  /**
   * Function that will triggre the state to reload when there is a change on any Property set by the user
   */
  #updatePropertySetColumnState = () => this.psColumnState.fetchSetData();

  /**
   * Function that will track operations made by the user so when closing the modal
   * the parent components can react properly according to these configurations
   * @param operation PropertySetOperation
   */
  #trackOperation = (operation: PropertySetOperation) => this.psOperatinsTracker.trackOperation(operation);

  public create(propertySet: PropertySetDto): Observable<PropertySet> {
    return this.client.post<PropertySet>('/api/propertyset', propertySet).pipe(
      take(1),
      tap(this.#updatePropertySetColumnState),
      tap(() => this.#trackOperation(PropertySetOperation.CreatedPS))
    );
  }

  public findAll(query?: PropertySetQuery): Observable<PropertySet[]> {
    const params = new HttpParams({ fromObject: query });
    return this.client.get<PropertySet[]>('/api/propertysets', { params }).pipe(take(1));
  }

  public findById(id: number): Observable<PropertySet> {
    return this.client.get<PropertySet>(`/api/propertyset/${id}`).pipe(take(1));
  }

  public update(id: number, propertySet: PropertySetDto): Observable<void> {
    return this.client.put<void>(`/api/propertyset/${id}`, propertySet).pipe(
      take(1),
      tap(this.#updatePropertySetColumnState),
      tap(() => this.#trackOperation(PropertySetOperation.UpdatedPS))
    );
  }

  public delete(id: number): Observable<void> {
    return this.client.delete<void>(`/api/propertyset/${id}`).pipe(
      take(1),
      tap(this.#updatePropertySetColumnState),
      tap(() => this.#trackOperation(PropertySetOperation.DeletedPS))
    );
  }

  public getPropertySetLookup(
    filters: Filter[] = [],
    options: Pick<PSSearchQuery, 'limit' | 'offset'> = { limit: 0, offset: 0 }
  ): Observable<any> {
    const payload = PSSearchQueryFactory({ filters, ...options });
    return this.client.post<any>('/api/propertysets/lookup', payload);
  }

  public getPropertySetByFilters(
    filters: Filter[] = [],
    options: Pick<PSSearchQuery, 'limit' | 'offset'> = { limit: 10 }
  ): Observable<PropertySet[]> {
    const payload = PSSearchQueryFactory({ filters, ...options });
    return this.client.post<any>('/api/search/propertysets', payload);
  }

  public getPropertySetProperties(request: any, limit?: number): Observable<RecordsResponse> {
    const generatedPayload = request;
    if (limit) {
      generatedPayload.limit = limit;
    }
    const ignoreHistoryFlag = `&ignoreHistory=${true}`;
    return this.client.post<RecordsResponse>(`/api/search?view=table${ignoreHistoryFlag}`, generatedPayload).pipe(
      take(1),
      catchError(error => {
        this.logger.error(`Error loading properties: ${error}`);
        return of({ totalCount: 0, results: [] });
      })
    );
  }

  public addPropertiesToPropertySet(propertySetId: number, usageIds: number[]): Observable<any> {
    return this.client.post(`/api/propertyset/${propertySetId}/records`, usageIds).pipe(
      take(1),
      tap(() => this.#trackOperation(PropertySetOperation.AddedPropertyToPS))
    );
  }

  public deletePropertiesToPropertySet(propertySetId: number, usageIds: number[]): Observable<any> {
    return this.client.delete(`/api/propertyset/${propertySetId}/records`, { body: usageIds }).pipe(
      take(1),
      tap(() => this.#trackOperation(PropertySetOperation.DeletedPropertyFromPS))
    );
  }

  public getPermissions(propertySetId: number): Observable<PropertySetPermission[]> {
    return this.client.get<PropertySetPermission[]>(`/api/propertyset/${propertySetId}/permission`).pipe(take(1));
  }

  /**
   * Get roles catalog.
   */
  public getRoles(): Observable<IOption[]> {
    return this.client.get<IOption[]>(`/api/lookup/roles`).pipe(take(1));
  }

  /**
   * Get GlobalRegions catalog.
   */
  public getGlobalRegions(): Observable<IOption[]> {
    return this.client.get<IOption[]>(`/api/geo/globalregions`).pipe(take(1));
  }

  /**
   * Get divisions catalog.
   */
  public getDivision(globalRegionId): Observable<IOption[]> {
    return this.client.get<IOption[]>(`/api/geo/divisions/${globalRegionId}`).pipe(take(1));
  }

  /**
   * Get markets catalog.
   */
  public getMarkets(divisionId): Observable<IOption[]> {
    return this.client.get<IOption[]>(`/api/geo/markets/${divisionId}`).pipe(take(1));
  }

  /**
   * Get property types catalog.
   */
  public getPropertyType(): Observable<IOption[]> {
    return this.client.get<IOption[]>(`/api/lookup/propertyusagetypes`).pipe(take(1));
  }

  /**
   * Get confidentiality level catalog.
   */
  public getConfidentialityLevel(): Observable<IOption[]> {
    return this.client.get<IOption[]>(`/api/lookup/user/confidentiality`).pipe(take(1));
  }

  /**
   * Get all MIQ users.
   */
  public getActiveUsers(): Observable<any> {
    return this.client.get(`/api/users?status=Active`).pipe(take(1));
  }

  public deletePropertySetPermission(permissionId: number): Observable<any> {
    return this.client.delete(`/api/propertyset/${permissionId}/permission`).pipe(
      take(1),
      tap(() => this.#trackOperation(PropertySetOperation.DeletedPSPermission))
    );
  }

  public savePropertySetPermission(permission: PropertySetPermission[], propertySetId: number): Observable<any> {
    return this.client.post(`/api/propertyset/${propertySetId}/permission`, permission).pipe(
      take(1),
      tap(() => this.#trackOperation(PropertySetOperation.AddedPSPermission))
    );
  }

  public updatePropertySetPermission(permission: PropertySetPermission[], propertySetId: number): Observable<any> {
    return this.client.put(`/api/propertyset/${propertySetId}/permission`, permission).pipe(
      take(1),
      tap(() => this.#trackOperation(PropertySetOperation.UpdatedPSPermission))
    );
  }

  public initHistoricalExport(query: DataObject): Observable<any> {
    return this.client.post('/api/export/init/stats', query);
  }

  public completeExport(exportID: number, failed = false) {
    const uri = `/api/export/${exportID}/complete?failed=${failed}`;
    return this.client.put(uri, null);
  }

  public bulkExport(exportObj: HistoricalStatQuery) {
    // There are 2 new features for PDF Server one that contains the Property Sets implementation and one for Hitorical Stats Filters
    let uri = '/api/download/miq/export/historicalstatscsv';
    if (TogglePointFunctionHelper.toggleRouterService.isFeatureEnabled('historicalStatPS')) {
      uri = '/api/download/historical-stats';
    }
    // the historical Stats version 2 has what we need for the PS too
    if (TogglePointFunctionHelper.toggleRouterService.isFeatureEnabled('historicalStatChanges')) {
      uri = '/api/download/v2/historical-stats';
    }

    return this.client.post(uri, exportObj, { responseType: 'text', observe: 'response' }).pipe(
      map(({ body, headers }: HttpResponse<any>) => ({
        data: body,
        fileName: headers.get('Content-Disposition').split('filename=')[1].split('"')[1],
      })),
      catchError(err => {
        return of({ exportId: null, recordData: [], err });
      })
    );
  }
}
